import {
  computed,
  createEnvironmentInjector,
  EnvironmentInjector,
  inject,
  Injectable,
  OnDestroy,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, firstValueFrom, switchMap, timer } from 'rxjs';
import { ToasterService } from '@kinomap-dev/ng-ui-lib/elements';
import { METRIC_MODE_BY_ID, REFRESH_INTERVAL_MS } from './contest-store.types';
import { LeaderboardService } from './leaderboard/leaderboard.service';
import { ContestInformationService } from './contest-information.service';
import { ContestStore } from './contest-store';
import { ContestApi, ContestRankingMode, LEADERBOARD_MODE } from './api/contest-api.types';
import { LeaderboardMetricId } from 'app/features/contest/models/contest-template.models';
import { isGenderType } from 'app/features/contest/models/gender';
import { ErrorCode, httpStatusToErrorCode } from 'app/shared/models/error.models';
import { LoginService } from 'app/core/auth/login.service';

@Injectable()
export class ContestService implements OnDestroy {
  private readonly environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly store: ContestStore = inject(ContestStore);
  private readonly api: ContestApi = inject(ContestApi);
  private readonly loginService: LoginService = inject(LoginService);
  private readonly toasterService: ToasterService = inject(ToasterService);
  private readonly contestInformationService: ContestInformationService =
    inject(ContestInformationService);

  private readonly rankingInjector = createEnvironmentInjector(
    [
      { provide: ContestStore, useValue: this.store },
      { provide: LEADERBOARD_MODE, useValue: 'top_ranking_ppr' },
      LeaderboardService,
    ],
    this.environmentInjector
  );

  private readonly historyInjector = createEnvironmentInjector(
    [
      { provide: ContestStore, useValue: this.store },
      { provide: LEADERBOARD_MODE, useValue: 'last_activities' },
      LeaderboardService,
    ],
    this.environmentInjector
  );

  readonly canManageContest: Signal<boolean> = computed(
    () => this.loginService.isAdmin() || this.loginService.username()?.toLowerCase() === this.store.username().toLowerCase()
  );

  isReady: WritableSignal<boolean> = signal<boolean>(false);
  selectedMetric: WritableSignal<LeaderboardMetricId> = signal<LeaderboardMetricId>(
    this.store.templateMetrics().default
  );
  rankingDisplayMode: WritableSignal<ContestRankingMode> = signal<ContestRankingMode>(
    METRIC_MODE_BY_ID[this.selectedMetric()]
  );
  deletingActivityIds: WritableSignal<ReadonlySet<number>> = signal<ReadonlySet<number>>(
    new Set<number>()
  );

  rankingLeaderboard: LeaderboardService = this.rankingInjector.get(LeaderboardService);
  historyLeaderboard: LeaderboardService = this.historyInjector.get(LeaderboardService);

  constructor() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed())
      .subscribe((params: Params) => void this.loadContestFromRouteParams(params));

    toObservable(this.isReady)
      .pipe(
        switchMap((isReady: boolean) => (isReady ? timer(0, REFRESH_INTERVAL_MS) : EMPTY)),
        takeUntilDestroyed()
      )
      .subscribe(() => void this.refreshData());
  }

  ngOnDestroy(): void {
    this.rankingInjector.destroy();
    this.historyInjector.destroy();
  }

  async loadContestFromRouteParams(params: Params): Promise<void> {
    this.store.username.set(params['username']);
    this.store.hashid.set(params['hashid']);

    try {
      await this.contestInformationService.loadContestInformation();
      this.applyTemplateDefaultMetric();
      this.isReady.set(true);
    } catch (error: unknown) {
      this.isReady.set(false);
      await this.router.navigate(['/error', this.getErrorCode(error)]);
    }
  }

  refreshData(): Promise<void> {
    return Promise.all([this.rankingLeaderboard.refresh(), this.historyLeaderboard.refresh()]).then(
      () => undefined
    );
  }

  async deleteActivity(activityId: number): Promise<void> {
    if (this.deletingActivityIds().has(activityId)) return;

    this.addDeletingActivity(activityId);

    try {
      await firstValueFrom(this.api.deleteActivity(activityId));
      await this.refreshData();
    } catch (error: unknown) {
      console.error(error);
      this.toasterService.error(
        'Suppression impossible',
        "L'activité n'a pas pu être supprimée. Veuillez réessayer."
      );
    } finally {
      this.removeDeletingActivity(activityId);
    }
  }

  selectMetric(metricId: LeaderboardMetricId): void {
    if (!this.isMetricAvailable(metricId)) return;

    this.selectedMetric.set(metricId);
    const mode: ContestRankingMode = METRIC_MODE_BY_ID[metricId];
    this.rankingDisplayMode.set(mode);
    this.rankingLeaderboard.changeMode(mode);
  }

  setPprFilter(pprFilter: number): void {
    this.store.pprFilter.set(pprFilter);
    this.resetLeaderboards();
  }

  setTargetGender(targetGender: string): void {
    if (!isGenderType(targetGender)) return;

    this.store.targetGender.set(targetGender);
    this.resetLeaderboards();
  }

  private resetLeaderboards(): void {
    this.rankingLeaderboard.reset();
    this.historyLeaderboard.reset();
  }

  private applyTemplateDefaultMetric(): void {
    this.selectMetric(this.store.templateMetrics().default);
  }

  private isMetricAvailable(metricId: LeaderboardMetricId): boolean {
    return this.store.templateMetrics().available.includes(metricId);
  }

  private addDeletingActivity(activityId: number): void {
    this.deletingActivityIds.update((activityIds: ReadonlySet<number>) =>
      new Set<number>(activityIds).add(activityId)
    );
  }

  private removeDeletingActivity(activityId: number): void {
    this.deletingActivityIds.update((activityIds: ReadonlySet<number>) => {
      const nextActivityIds: Set<number> = new Set<number>(activityIds);

      nextActivityIds.delete(activityId);

      return nextActivityIds;
    });
  }

  private getErrorCode(error: unknown): ErrorCode {
    if (!(error instanceof HttpErrorResponse)) return ErrorCode.UNKNOWN_ERROR;
    if (error.status === 404) return ErrorCode.LEADERBOARD_NOT_FOUND;

    return httpStatusToErrorCode(error.status) ?? ErrorCode.UNKNOWN_ERROR;
  }
}
